import { useMemo } from 'react';

import type { Artist } from '../interfaces/maestra';
import { ACENTO_DO_PRODUTO } from '../constants/produtos';
import { isOnboardingComplete } from '../constants/maestra';
import { useArtistCapabilities } from './useArtistCapabilities';

// Estágio do ciclo de crescimento do artista. Uma fonte única de verdade pro "cada produto libera
// o próximo": Diagnóstico REAL → Planejamento (Nyta) → Plano de Ação → ↺ evolução.
export type JourneyStage = 'diagnostic' | 'plan' | 'tasks' | 'evolution';

export interface JourneyNext {
  stage: JourneyStage;
  kicker: string;     // rótulo do produto (ex.: "Planejamento")
  title: string;      // chamada principal ("Crie seu planejamento com a Nyta")
  desc: string;       // 1 linha de apoio
  ctaLabel: string;   // texto do botão
  to: string;         // sufixo da rota (prefixar com /artists/:id/), ex.: 'wizard'
  accent: string;     // triplete "r, g, b" do produto (ACENTO_DO_PRODUTO)
}

export interface JourneyState {
  paid: boolean;
  hasDiagnostic: boolean;
  hasPlan: boolean;
  /** Começou o planejamento e não concluiu: o convite é "continuar", não "criar". */
  resumingPlan: boolean;
  tasksTotal: number;
  tasksDone: number;
  tasksPending: number;
  pct: number;
  stage: JourneyStage;
  next: JourneyNext;
}

const REAL = ACENTO_DO_PRODUTO.real;
const PLAN = ACENTO_DO_PRODUTO.planning;
const ACTION = ACENTO_DO_PRODUTO.action;

// Deriva o estado da jornada a partir do conteúdo do artista + capacidades (pago/PRO).
// Reusado pelo card "Seu próximo passo", pelo JourneyMap e pela Sidebar (cadeados suaves).
export function useJourneyState(artist?: Artist | null): JourneyState {
  const { isPaid, viewPlanning } = useArtistCapabilities(artist);

  return useMemo(() => {
    const c = artist?.content;
    const paid = !!viewPlanning && !!isPaid;
    const hasDiagnostic = !!c?.realIndex?.profile;
    const strategies = c?.strategies || [];
    // "Plano pronto" = wizard CONCLUÍDO (Finalizar clicado, step >= total). NÃO basta ter
    // estratégias geradas (step 6): faltam a seleção que vira tarefa (step 7) e o resumo (step 8).
    // Sem isto, sair do wizard após gerar as estratégias fazia o app pular direto pro plano de ação.
    const hasPlan = isOnboardingComplete(artist);
    // Já começou o planejamento (mesmo sem concluir) → CTA vira "continuar", não "criar".
    const resumingPlan = !hasPlan && ((c?.step ?? 0) > 0 || strategies.length > 0);

    const allTasks = strategies.flatMap((s) => (s.tasks || []).filter((t) => t.status !== 'archived'));
    const tasksTotal = allTasks.length;
    const tasksDone = allTasks.filter((t) => t.status === 'done').length;
    const tasksPending = tasksTotal - tasksDone;
    const pct = tasksTotal ? Math.round((tasksDone / tasksTotal) * 100) : 0;

    let next: JourneyNext;
    if (!hasDiagnostic) {
      next = {
        stage: 'diagnostic', kicker: 'Diagnóstico REAL', accent: REAL, to: 'diagnostico',
        title: 'Comece pelo seu Diagnóstico REAL',
        desc: 'Descubra em que fase a sua carreira está, com dados reais.',
        ctaLabel: 'Ver diagnóstico',
      };
    } else if (!hasPlan) {
      next = resumingPlan
        ? {
            stage: 'plan', kicker: 'Planejamento', accent: PLAN, to: 'wizard',
            title: 'Continue seu planejamento com a Nyta',
            desc: 'Você parou no meio. Volte pra escolher as estratégias e finalizar o plano.',
            ctaLabel: 'Continuar planejamento',
          }
        : {
            stage: 'plan', kicker: 'Planejamento', accent: PLAN, to: 'wizard',
            title: 'Crie seu planejamento com a Nyta',
            desc: 'Defina visão, missão, objetivos e estratégias — o mapa da sua carreira.',
            ctaLabel: 'Criar planejamento',
          };
    } else if (tasksPending > 0 || tasksTotal === 0) {
      next = {
        stage: 'tasks', kicker: 'Plano de Ação', accent: ACTION, to: 'action-plan',
        title: tasksTotal === 0 ? 'Monte suas ações no Plano de Ação' : `Suas ações de hoje · ${tasksPending} pendente${tasksPending === 1 ? '' : 's'}`,
        desc: tasksTotal === 0 ? 'Transforme suas estratégias em passos práticos.' : 'Avance um passo de cada vez e veja sua carreira andar.',
        ctaLabel: tasksTotal === 0 ? 'Abrir plano' : 'Ver minhas ações',
      };
    } else {
      next = {
        stage: 'evolution', kicker: 'Evolução', accent: REAL, to: 'diagnostico/refazer',
        title: 'Tudo em dia! Hora de medir sua evolução',
        desc: 'Você concluiu suas ações. Refaça o diagnóstico e veja o quanto avançou.',
        ctaLabel: 'Refazer diagnóstico',
      };
    }

    return { paid, hasDiagnostic, hasPlan, resumingPlan, tasksTotal, tasksDone, tasksPending, pct, stage: next.stage, next };
  }, [artist, isPaid, viewPlanning]);
}

export default useJourneyState;
